import { VerificationEvent, VerificationFlowState, VerificationStep, PartnerType } from './types'

// Helper function to get current membership based on partner type
const getCurrentMembership = (state: VerificationFlowState) => {
  return state.currentPartnerType === 'RealEstateAgent' ? state.realEstateAgentMembership : state.loanOfficerMembership
}

// Helper function to determine next step after verification success
const getNextStepAfterVerification = (state: VerificationFlowState): VerificationStep => {
  // If prequalOnly is true, skip messaging and go directly to prequal
  if (state.prequalOnly) {
    return 'sendPrequal'
  }
  return 'messaging'
}

// Helper function to determine next step after messaging success
const getNextStepAfterMessaging = (state: VerificationFlowState): VerificationStep => {
  if (state.includePrequal) {
    // Show prequal if customer is LO OR if there's an LO membership
    const shouldShowPrequal = state.customerIsLoanOfficer || state.loanOfficerMembership
    return shouldShowPrequal ? 'startPrequal' : 'complete'
  }
  return 'complete'
}

// Helper function to determine what happens when canceling from addPartner
const getNextStepAfterAddPartnerCancel = (state: VerificationFlowState): VerificationStep => {
  if (state.includePrequal && (state.customerIsLoanOfficer || state.loanOfficerMembership)) {
    return 'startPrequal'
  }
  return 'idle'
}

const getNextStepAfterStartPrequal = (state: VerificationFlowState): VerificationStep => {
  // If customer is LO, go directly to sendPrequal (send to customer)
  if (state.customerIsLoanOfficer) {
    return 'sendPrequal'
  }

  // Otherwise, existing logic for external LO membership
  if (
    state.currentPartnerType === 'RealEstateAgent' &&
    state.loanOfficerMembership &&
    !state.loanOfficerMembership.isVerified
  ) {
    return 'verificationPrequal'
  }
  return 'sendPrequal'
}

// Define possible state transitions
export const flowMachine = {
  initial: 'idle' as VerificationStep,
  states: {
    idle: {
      on: {
        START_VERIFICATION: (state: VerificationFlowState) => {
          const membership = getCurrentMembership(state)
          if (!membership) {
            return 'idle'
          }
          // For prequalOnly flows with unverified LO membership, go directly to verificationPrequal
          if (state.prequalOnly && state.currentPartnerType === 'LoanOfficer' && !membership.isVerified) {
            return 'verificationPrequal'
          }
          return 'verifying'
        },
        SEND_MESSAGE: 'messaging'
      }
    },
    verifying: {
      on: {
        SUCCESS: (state: VerificationFlowState) => getNextStepAfterVerification(state),
        FAILURE: 'unverified',
        CANCEL: 'idle'
      }
    },
    unverified: {
      on: {
        ADD_PARTNER: (state: VerificationFlowState) => getNextStepAfterVerification(state),
        REMOVE_PARTNER: 'removePartner',
        SEND_PREQUAL: 'sendPrequal',
        SKIP_PARTNER_VERIFICATION: 'complete',
        PREQUAL_SENT: 'complete',
        CANCEL: 'idle'
      }
    },
    addPartner: {
      on: {
        PARTNER_ADDED: (state: VerificationFlowState) => getNextStepAfterVerification(state),
        CANCEL: (state: VerificationFlowState) => getNextStepAfterAddPartnerCancel(state)
      }
    },
    removePartner: {
      on: {
        PARTNER_REMOVED: 'addPartner',
        ADD_PARTNER: 'addPartner',
        CANCEL: 'unverified'
      }
    },
    messaging: {
      on: {
        START_VERIFICATION: 'verifying',
        CANCEL: 'idle',
        MESSAGE_SUCCESS: (state: VerificationFlowState) => getNextStepAfterMessaging(state)
      }
    },
    startPrequal: {
      on: {
        START_NEXT_PARTNER_VERIFICATION: (state: VerificationFlowState) => getNextStepAfterStartPrequal(state),
        SKIP_PARTNER_VERIFICATION: 'complete'
      }
    },
    verificationPrequal: {
      on: {
        CANCEL: 'complete',
        PREQUAL_SENT: 'complete',
        REMOVE_PARTNER: 'removePartner',
        SKIP_PARTNER_VERIFICATION: 'complete'
      }
    },
    sendPrequal: {
      on: {
        PREQUAL_SENT: 'complete',
        CANCEL: 'complete'
      }
    },
    complete: {
      on: {
        RESET: 'idle'
      }
    }
  }
}

// Flow reducer to manage state transitions
export const flowReducer = (state: VerificationFlowState, event: VerificationEvent): VerificationFlowState => {
  const currentStep = state.currentStep
  const currentPartnerType = state.currentPartnerType

  // Special case for membership updates - don't change step
  if (event.type === 'UPDATE_MEMBERSHIP') {
    const { membership, partnerType } = event.payload
    return {
      ...state,
      ...(partnerType === 'RealEstateAgent'
        ? { realEstateAgentMembership: membership }
        : { loanOfficerMembership: membership })
    }
  }

  // Special case for customer profile updates - don't change step
  if (event.type === 'UPDATE_CUSTOMER_PROFILE') {
    const { customerIsLoanOfficer } = event.payload
    return {
      ...state,
      customerIsLoanOfficer
    }
  }

  // Get the transition definition from the flow machine
  const transition = flowMachine.states[currentStep]?.on?.[event.type]

  // Handle function transitions
  let nextStep: VerificationStep | undefined
  if (typeof transition === 'function') {
    // Use the function to determine the next state based on current state
    nextStep = transition(state)
  } else {
    // Otherwise use the string value directly
    nextStep = transition as VerificationStep | undefined
  }
  // If no valid transition is found, return current state
  if (!nextStep) {
    return state
  }

  // Handle specific event types with payload
  switch (event.type) {
    case 'START_VERIFICATION':
      return {
        ...state,
        currentStep: nextStep,
        message: event.payload.message,
        topicKey: event.payload.topicKey,
        metadata: event.payload.metadata,
        // Update partner types if needed
        partnerType: event.payload.partnerType || state.partnerType,
        currentPartnerType: event.payload.partnerType || state.currentPartnerType
      }

    case 'START_NEXT_PARTNER_VERIFICATION':
      // Switch from RE agent to LO verification
      return {
        ...state,
        currentStep: nextStep,
        currentPartnerType: 'LoanOfficer',
        isVerified: false // Reset verification status for the new partner type
      }

    case 'SUCCESS':
      return {
        ...state,
        currentStep: nextStep,
        isVerified: true
      }
    case 'FAILURE':
      return {
        ...state,
        currentStep: nextStep,
        isVerified: false
      }

    case 'PARTNER_ADDED':
      // Get partner data and membership from payload
      const partnerData = event.payload?.partnerData || {}
      const membership = event.payload?.membership
      const addedPartnerType = event.payload?.partnerType

      // Set the partner type if present in payload, otherwise keep current
      const newPartnerType = addedPartnerType || (partnerData.type as PartnerType) || state.partnerType

      // Update the appropriate membership based on partner type
      const updatedState = {
        ...state,
        currentStep: nextStep,
        partnerData: partnerData,
        partnerType: newPartnerType,
        removedPartnerType: undefined, // Clear removed partner type
        isVerified: true // Mark as verified after adding a partner
      }

      // Update the correct membership field based on partner type
      if (addedPartnerType === 'RealEstateAgent') {
        updatedState.realEstateAgentMembership = membership
      } else if (addedPartnerType === 'LoanOfficer') {
        updatedState.loanOfficerMembership = membership
      }

      return updatedState

    case 'PARTNER_REMOVED':
      return {
        ...state,
        currentStep: nextStep,
        partnerData: event.payload?.partnerData,
        removedPartnerType: event.payload?.removedPartnerType,
        isVerified: false
      }
    case 'PREQUAL_SENT':
      return {
        ...state,
        currentStep: nextStep,
        partnerData: event.payload?.partnerData,
        isVerified: true
      }
    case 'SEND_MESSAGE':
      return {
        ...state,
        currentStep: nextStep,
        message: event.payload.message,
        topicKey: event.payload.topicKey,
        metadata: event.payload.metadata,
        partnerType: event.payload.partnerType || state.partnerType
      }
    case 'CANCEL':
      // Clear removed partner type when canceling
      return {
        ...state,
        currentStep: nextStep,
        removedPartnerType: undefined
      }
    default:
      // For simple transitions without payload
      return {
        ...state,
        currentStep: nextStep
      }
  }
}
